import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineView } from "foldkit/submodel";

import { toMessage } from "../../api/errors";
import type {
	ImportBatch,
	MatchCandidate,
	UndoPreview,
} from "../../api/schemas/import";
import { Route, linkHref } from "../../app/routes";
import { i18next } from "../../i18n";
import type { ParsedContact } from "../../import/contact";
import { confirmDialog } from "../../widgets/dialog";
import * as Icons from "../../widgets/icons";
import { formatDateTime } from "../auditLog/view";
import type { Shared } from "../shared";
import { Message } from "./message";
import {
	type Model,
	PAGE_SIZE,
	type ReviewRow,
	canConfirm,
	commitPlan,
	filteredContacts,
	matchByRow,
	overflow,
	reviewRows,
	summary,
	unresolvedRows,
} from "./model";

/**
 * The wizard's five steps. The root carries `ph-no-capture` so session replay
 * masks every name, email and phone the preview renders (KTD1).
 */

const t = (key: string, values?: Record<string, unknown>): string =>
	i18next.t(key, values ?? {});

const creditsHref = (shared: Shared): string =>
	linkHref(
		shared.route,
		Route.Credits({ asOf: Option.none(), sessionId: Option.none() }),
	);

const recordsHref = (shared: Shared): string =>
	linkHref(
		shared.route,
		Route.Records({ asOf: Option.none(), q: Option.none() }),
	);

const isOnboarding = (shared: Shared): boolean =>
	shared.route._tag === "Import" && Option.isSome(shared.route.onboarding);

const alert = (h: HtmlBuilder<Message>, text: string): Html =>
	h.div(
		[h.Role("alert"), h.Class("alert alert-error")],
		[
			Icons.AlertCircle(h, { class: "h-5 w-5 shrink-0 stroke-current" }),
			h.span([], [text]),
		],
	);

const parseErrorText = (model: Model): Option.Option<string> =>
	Option.map(model.parseError, (error) =>
		t(`import.error.${error.kind}`, {
			row: Option.getOrElse(error.row, () => 0),
		}),
	);

// PICK

const pick = (h: HtmlBuilder<Message>, model: Model): Html =>
	h.div(
		[h.Class("flex flex-col gap-4 rounded-xl border border-base-300 p-6")],
		[
			h.input([
				h.Type("file"),
				h.Class("file-input file-input-bordered w-full"),
				h.AriaLabel(t("import.chooseFile")),
				h.Attribute("accept", ".csv,.vcf,text/csv,text/vcard"),
				h.OnFileChange((files) =>
					Message.ChangedFile({ file: Option.fromNullishOr(files[0]) }),
				),
			]),
			h.p([h.Class("text-sm text-base-content/70")], [t("import.chooseHelp")]),
			Option.match(parseErrorText(model), {
				onNone: () => h.empty,
				onSome: (text) => alert(h, text),
			}),
		],
	);

// PREVIEW

const previewRow = (
	h: HtmlBuilder<Message>,
	model: Model,
	contact: ParsedContact,
	present: boolean,
): Html =>
	h.keyed("tr")(
		String(contact.rowId),
		[],
		[
			h.td(
				[],
				[
					h.input([
						h.Type("checkbox"),
						h.Class("checkbox checkbox-sm"),
						h.AriaLabel(t("import.preview.selectRow", { name: contact.name })),
						h.Checked(model.selected.has(contact.rowId)),
						h.OnClick(Message.ToggledRow({ rowId: contact.rowId })),
					]),
				],
			),
			h.td(
				[],
				[
					h.span([h.Class("font-medium")], [contact.name]),
					present
						? h.span(
								[h.Class("badge badge-ghost badge-sm ml-2")],
								[t("import.preview.present")],
							)
						: h.empty,
				],
			),
			h.td([h.Class("text-base-content/70")], [contact.organisation ?? ""]),
			h.td([h.Class("text-base-content/70")], [contact.emails[0] ?? ""]),
		],
	);

const pager = (h: HtmlBuilder<Message>, model: Model, pages: number): Html => {
	if (pages <= 1) return h.empty;
	return h.div(
		[h.Class("flex items-center gap-3 pt-3")],
		[
			h.button(
				[
					h.Type("button"),
					h.Class("btn btn-sm"),
					h.AriaLabel(t("import.preview.previous")),
					h.Disabled(model.pageIndex === 0),
					h.OnClick(Message.ChangedPage({ page: model.pageIndex - 1 })),
				],
				["‹"],
			),
			h.span(
				[h.Class("text-sm")],
				[
					t("import.preview.page", {
						page: model.pageIndex + 1,
						pages,
					}),
				],
			),
			h.button(
				[
					h.Type("button"),
					h.Class("btn btn-sm"),
					h.AriaLabel(t("import.preview.next")),
					h.Disabled(model.pageIndex >= pages - 1),
					h.OnClick(Message.ChangedPage({ page: model.pageIndex + 1 })),
				],
				["›"],
			),
		],
	);
};

const preview = (h: HtmlBuilder<Message>, model: Model): Html => {
	// One pass over the parsed rows, reused by the page slice, the pager and the
	// select-all box. The filter re-renders on every keystroke and the file can
	// hold 20,000 rows, so recomputing this per use is the whole render budget.
	const filtered = filteredContacts(model);
	const visible = filtered.slice(
		model.pageIndex * PAGE_SIZE,
		model.pageIndex * PAGE_SIZE + PAGE_SIZE,
	);
	const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const allSelected =
		filtered.length > 0 &&
		filtered.every((contact) => model.selected.has(contact.rowId));
	const byRow = matchByRow(model);
	const matching =
		AsyncData.isLoading(model.matches) || AsyncData.isRefreshing(model.matches);
	return h.div(
		[h.Class("flex flex-col gap-4")],
		[
			h.h2(
				[h.Class("text-lg font-semibold")],
				[
					t("import.preview.title", {
						count: model.contacts.length,
						file: model.fileName,
					}),
				],
			),
			model.excluded.noName + model.excluded.duplicate === 0
				? h.empty
				: h.p(
						[h.Class("text-sm text-base-content/70")],
						[
							t("import.preview.excluded", {
								count: model.excluded.noName + model.excluded.duplicate,
							}),
						],
					),
			h.input([
				h.Type("text"),
				h.Class("input input-bordered w-full max-w-md"),
				h.AriaLabel(t("import.preview.filter")),
				h.Placeholder(t("import.preview.filter")),
				h.Value(model.filter),
				h.OnInput((value) => Message.ChangedFilter({ value })),
			]),
			h.div(
				[h.Class("flex items-center gap-3")],
				[
					h.input([
						h.Type("checkbox"),
						h.Class("checkbox checkbox-sm"),
						h.AriaLabel(t("import.preview.selectAll")),
						h.Checked(allSelected),
						h.OnClick(Message.ToggledSelectAll()),
					]),
					h.span(
						[h.Class("text-sm")],
						[t("import.preview.selectedCount", { count: model.selected.size })],
					),
				],
			),
			visible.length === 0
				? h.p([h.Class("text-sm")], [t("import.preview.empty")])
				: h.div(
						// A solid surface: the page's dotted background shows through
						// every gap in a bare table and makes a long list hard to read.
						[
							h.Class(
								"overflow-x-auto rounded-xl border border-base-300 bg-base-100",
							),
						],
						[
							h.table(
								[h.Class("table table-sm table-zebra")],
								[
									h.thead(
										[],
										[
											h.tr(
												[],
												[
													h.th([], []),
													h.th([], [t("import.preview.columnName")]),
													h.th([], [t("import.preview.columnOrganisation")]),
													h.th([], [t("import.preview.columnEmail")]),
												],
											),
										],
									),
									h.tbody(
										[],
										visible.map((contact) =>
											previewRow(
												h,
												model,
												contact,
												byRow.get(contact.rowId)?.alreadyInCircle !== undefined,
											),
										),
									),
								],
							),
						],
					),
			pager(h, model, pages),
			AsyncData.isFailure(model.matches)
				? h.div(
						[h.Class("flex items-center gap-3")],
						[
							alert(h, t("import.preview.matchFailed")),
							h.button(
								[
									h.Type("button"),
									h.Class("btn btn-sm"),
									h.AriaLabel(t("import.preview.retry")),
									h.OnClick(Message.ClickedRetryMatch()),
								],
								[t("import.preview.retry")],
							),
						],
					)
				: h.empty,
			h.div(
				[h.Class("flex items-center gap-3")],
				[
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-primary"),
							h.AriaLabel(t("import.preview.continue")),
							h.Disabled(model.selected.size === 0 || matching),
							h.OnClick(Message.ClickedContinue()),
						],
						[t("import.preview.continue")],
					),
					matching
						? h.span(
								[h.Role("status"), h.Class("text-sm text-base-content/70")],
								[t("import.preview.matching")],
							)
						: h.empty,
				],
			),
		],
	);
};

// REVIEW

const choice = (
	h: HtmlBuilder<Message>,
	model: Model,
	rowId: number,
	label: string,
	action: "merge" | "create" | "skip",
	targetRecordId: Option.Option<string>,
): Html => {
	const current = model.resolutions.get(rowId);
	const checked =
		current !== undefined &&
		current.action === action &&
		Option.getOrNull(current.targetRecordId) ===
			Option.getOrNull(targetRecordId);
	return h.label(
		[h.Class("flex items-center gap-2")],
		[
			h.input([
				h.Type("radio"),
				h.Class("radio radio-sm"),
				h.Name(`import-row-${rowId}`),
				h.AriaLabel(label),
				h.Checked(checked),
				h.OnClick(Message.ChoseResolution({ rowId, action, targetRecordId })),
			]),
			h.span([h.Class("text-sm")], [label]),
		],
	);
};

const candidateLabel = (candidate: MatchCandidate): string =>
	t("import.review.mergeInto", {
		name:
			candidate.organisation === undefined
				? candidate.name
				: `${candidate.name} — ${candidate.organisation}`,
	});

const reviewCard = (
	h: HtmlBuilder<Message>,
	model: Model,
	row: ReviewRow,
): Html =>
	h.keyed("div")(
		String(row.contact.rowId),
		[h.Class("rounded-xl border border-base-300 p-4")],
		[
			h.p([h.Class("font-semibold")], [row.contact.name]),
			h.p(
				[h.Class("text-sm text-base-content/70")],
				[row.contact.organisation ?? row.contact.emails[0] ?? ""],
			),
			h.div(
				[h.Class("mt-3 flex flex-col gap-2")],
				[
					...row.candidates.map((candidate) =>
						choice(
							h,
							model,
							row.contact.rowId,
							candidateLabel(candidate),
							"merge",
							Option.some(candidate.recordId),
						),
					),
					choice(
						h,
						model,
						row.contact.rowId,
						t("import.review.createNew"),
						"create",
						Option.none(),
					),
					choice(
						h,
						model,
						row.contact.rowId,
						t("import.review.skipRow"),
						"skip",
						Option.none(),
					),
				],
			),
		],
	);

const bulkButton = (
	h: HtmlBuilder<Message>,
	key: string,
	action: "merge" | "create" | "skip",
): Html =>
	h.button(
		[
			h.Type("button"),
			h.Class("btn btn-sm"),
			h.AriaLabel(t(key)),
			h.OnClick(Message.ClickedBulk({ action })),
		],
		[t(key)],
	);

const review = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
): Html => {
	const rows = reviewRows(model);
	const plan = commitPlan(model);
	const short = overflow(model, plan);
	const pending = unresolvedRows(model, rows).length;
	const confirmable = canConfirm(model, plan, pending, short);
	return h.div(
		[h.Class("flex flex-col gap-4")],
		[
			h.h2([h.Class("text-lg font-semibold")], [t("import.review.title")]),
			rows.length === 0
				? h.p([h.Class("text-sm")], [t("import.review.none")])
				: h.div(
						[h.Class("flex flex-col gap-4")],
						[
							h.div(
								[h.Class("flex flex-wrap gap-2")],
								[
									bulkButton(h, "import.review.bulkMerge", "merge"),
									bulkButton(h, "import.review.bulkCreate", "create"),
									bulkButton(h, "import.review.bulkSkip", "skip"),
								],
							),
							h.div(
								[h.Class("flex flex-col gap-3")],
								rows.map((row) => reviewCard(h, model, row)),
							),
						],
					),
			pending === 0
				? h.empty
				: h.p(
						[h.Class("text-sm text-warning")],
						[t("import.review.unresolved", { count: pending })],
					),
			short === 0
				? h.empty
				: alert(h, t("import.review.capacity", { count: short })),
			short > 0 && shared.billingPreviewEnabled
				? h.a(
						[
							h.Href(creditsHref(shared)),
							h.Class("btn btn-sm btn-primary w-fit"),
						],
						[t("limits.seePlans")],
					)
				: h.empty,
			h.div(
				[h.Class("flex items-center gap-3")],
				[
					h.button(
						[
							h.Type("button"),
							h.Class("btn"),
							h.AriaLabel(t("import.review.back")),
							h.OnClick(Message.ClickedBack()),
						],
						[t("import.review.back")],
					),
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-primary"),
							h.AriaLabel(
								t("import.review.confirm", {
									creates: plan.creates,
									merges: plan.merges,
								}),
							),
							h.Disabled(!confirmable),
							h.OnClick(Message.ClickedConfirm()),
						],
						[
							t("import.review.confirm", {
								creates: plan.creates,
								merges: plan.merges,
							}),
						],
					),
				],
			),
		],
	);
};

// COMMITTING AND SUMMARY

const committing = (h: HtmlBuilder<Message>, model: Model): Html => {
	const counts = summary(model);
	return h.div(
		[h.Role("status"), h.Class("flex flex-col gap-3")],
		[
			h.h2([h.Class("text-lg font-semibold")], [t("import.committing.title")]),
			h.progress([
				h.Class("progress progress-primary w-full max-w-md"),
				h.Attribute("value", String(counts.landed)),
				h.Attribute("max", String(Math.max(1, counts.total))),
			]),
			h.p(
				[h.Class("text-sm")],
				[
					t("import.committing.progress", {
						landed: counts.landed,
						total: counts.total,
					}),
				],
			),
			h.p([h.Class("text-sm opacity-70")], [t("import.committing.background")]),
		],
	);
};

const line = (h: HtmlBuilder<Message>, key: string, count: number): Html =>
	count === 0 ? h.empty : h.li([], [t(key, { count })]);

const summaryStep = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
): Html => {
	const counts = summary(model);
	return h.div(
		[h.Class("flex flex-col gap-4")],
		[
			h.h2([h.Class("text-lg font-semibold")], [t("import.summary.title")]),
			h.ul(
				[h.Class("list-disc pl-5 text-sm")],
				[
					line(h, "import.summary.created", counts.created),
					line(h, "import.summary.merged", counts.merged),
					line(h, "import.summary.skipped", counts.skipped),
					line(h, "import.summary.excluded", counts.excluded),
					line(h, "import.summary.failed", counts.failed),
					line(h, "import.summary.notAttempted", counts.notAttempted),
				],
			),
			counts.notAttempted === 0
				? h.empty
				: h.p(
						[h.Role("alert"), h.Class("text-sm text-warning")],
						[
							t("import.summary.stopped", {
								reason: Option.getOrElse(model.stoppedReason, () =>
									t("import.summary.unknownReason"),
								),
							}),
						],
					),
			h.div(
				[h.Class("flex flex-wrap items-center gap-3")],
				[
					h.a(
						[h.Href(recordsHref(shared)), h.Class("btn btn-primary")],
						[t("import.summary.goToRecords")],
					),
					h.button(
						[
							h.Type("button"),
							h.Class("btn"),
							h.AriaLabel(t("import.summary.another")),
							h.OnClick(Message.ClickedRestart()),
						],
						[t("import.summary.another")],
					),
					historyLink(h, t("import.summary.viewHistory")),
				],
			),
		],
	);
};

// HISTORY AND UNDO

const historyLink = (h: HtmlBuilder<Message>, label: string): Html =>
	h.button(
		[
			h.Type("button"),
			h.Class("btn btn-ghost btn-sm"),
			h.AriaLabel(label),
			h.OnClick(Message.ClickedHistory()),
		],
		[label],
	);

/** `undone` offers no undo; `incomplete` and `stopped` still do (R18). */
const batchRow = (h: HtmlBuilder<Message>, batch: ImportBatch): Html => {
	const when = formatDateTime(batch.datetime);
	return h.keyed("tr")(
		batch.batchId,
		[],
		[
			h.td([], [t(`import.history.source.${batch.source}`)]),
			h.td([], [when]),
			h.td(
				[h.Class("text-base-content/70")],
				[
					t("import.history.counts", {
						created: batch.created,
						merged: batch.updated,
						entries: batch.entries,
						skipped: batch.skippedRows,
					}),
				],
			),
			h.td(
				[],
				[
					h.span(
						[h.Class("badge badge-ghost badge-sm")],
						[t(`import.history.state.${batch.state}`)],
					),
				],
			),
			h.td(
				[],
				[
					batch.state === "undone"
						? h.empty
						: h.button(
								[
									h.Type("button"),
									h.Class("btn btn-sm"),
									h.AriaLabel(t("import.history.undo", { when })),
									h.OnClick(Message.ClickedUndo({ batchId: batch.batchId })),
								],
								[t("import.history.undo", { when })],
							),
				],
			),
		],
	);
};

const batchTable = (
	h: HtmlBuilder<Message>,
	batches: ReadonlyArray<ImportBatch>,
): Html =>
	batches.length === 0
		? h.p([h.Class("text-sm")], [t("import.history.empty")])
		: h.div(
				[
					h.Class(
						"overflow-x-auto rounded-xl border border-base-300 bg-base-100",
					),
				],
				[
					h.table(
						[h.Class("table table-sm table-zebra")],
						[
							h.thead(
								[],
								[
									h.tr(
										[],
										[
											h.th([], [t("import.history.columnSource")]),
											h.th([], [t("import.history.columnWhen")]),
											h.th([], [t("import.history.columnCounts")]),
											h.th([], [t("import.history.columnState")]),
											h.th([], []),
										],
									),
								],
							),
							h.tbody(
								[],
								batches.map((batch) => batchRow(h, batch)),
							),
						],
					),
				],
			);

const history = (h: HtmlBuilder<Message>, model: Model): Html =>
	h.div(
		[h.Class("flex flex-col gap-4")],
		[
			h.h2([h.Class("text-lg font-semibold")], [t("import.history.title")]),
			AsyncData.match(model.batches, {
				onIdle: () => h.empty,
				onLoading: () =>
					h.p(
						[h.Role("status"), h.Class("text-sm")],
						[t("import.history.loading")],
					),
				onFailure: (error) =>
					alert(h, t("import.history.error", { message: toMessage(error) })),
				onRefreshing: ({ batches }) => batchTable(h, batches),
				onStale: ({ data }) => batchTable(h, data.batches),
				onSuccess: ({ batches }) => batchTable(h, batches),
			}),
			h.button(
				[
					h.Type("button"),
					h.Class("btn"),
					h.AriaLabel(t("import.history.back")),
					h.OnClick(Message.ClickedRestart()),
				],
				[t("import.history.back")],
			),
		],
	);

/** One line per class of record the undo touches, zeroes left out (R19). */
const undoLines = (preview: UndoPreview): ReadonlyArray<string> =>
	(
		[
			["import.history.undoCreated", preview.created],
			["import.history.undoMerged", preview.updated],
			["import.history.undoEdited", preview.editedSince],
			["import.history.undoWithEntries", preview.withEntries],
			["import.history.undoMergedAway", preview.mergedAway],
			["import.history.undoMissing", preview.missing],
		] as const
	)
		.filter(([, count]) => count > 0)
		.map(([key, count]) => t(key, { count }))
		.concat(t("import.history.undoQuota"));

const undoDialog = (h: HtmlBuilder<Message>, model: Model): Html => {
	const failure = Option.map(model.undoError, (message) =>
		t("import.history.undoFailed", { message }),
	);
	const settled = AsyncData.match(model.undoPreview, {
		onIdle: () => ({ message: t("import.history.undoLead"), lines: [] }),
		onLoading: () => ({ message: t("import.history.undoLoading"), lines: [] }),
		onFailure: (error) => ({
			message: t("import.history.undoPreviewFailed", {
				message: toMessage(error),
			}),
			lines: [],
		}),
		onRefreshing: (preview) => ({
			message: t("import.history.undoLead"),
			lines: undoLines(preview),
		}),
		onStale: ({ data }) => ({
			message: t("import.history.undoLead"),
			lines: undoLines(data),
		}),
		onSuccess: (preview) => ({
			message: t("import.history.undoLead"),
			lines: undoLines(preview),
		}),
	});
	return confirmDialog(h, {
		slotId: "confirm-import-undo",
		model: model.dialog,
		toParentMessage: (message) => Message.GotDialogMessage({ message }),
		title: t("import.history.undoTitle"),
		message: settled.message,
		lines: [
			...settled.lines,
			...Option.match(failure, { onNone: () => [], onSome: (text) => [text] }),
		],
		confirmLabel: t("import.history.undoConfirm"),
		cancelLabel: t("import.history.undoCancel"),
		onConfirm: Message.ClickedConfirmUndo(),
		// Confirm stays out of reach until the preview has arrived.
		isPending: model.undoing || !AsyncData.isSuccess(model.undoPreview),
	});
};

export const view = defineView<Model, Message, Shared>((model, shared, h) =>
	h.div(
		[
			h.DataAttribute("testid", "page-Import"),
			// Masks the third-party names, emails and phones below from session replay.
			h.Class(
				"ph-no-capture mx-auto my-4 flex max-w-4xl flex-col gap-6 rounded-2xl border border-base-300 bg-base-100 p-4 shadow-sm sm:p-6",
			),
		],
		[
			h.div(
				[h.Class("flex items-start justify-between gap-4")],
				[
					h.div(
						[],
						[
							h.h1([h.Class("text-2xl font-bold")], [t("import.title")]),
							h.p(
								[h.Class("mt-1 text-sm text-base-content/70")],
								[t("import.description")],
							),
						],
					),
					h.div(
						[h.Class("flex items-center gap-2")],
						[
							// Only from Pick: from any later step it would throw work away.
							model.step === "Pick"
								? historyLink(h, t("import.history.link"))
								: h.empty,
							isOnboarding(shared)
								? h.a(
										[
											h.Href(recordsHref(shared)),
											h.Class("btn btn-ghost btn-sm"),
										],
										[t("import.skip")],
									)
								: h.empty,
						],
					),
				],
			),
			model.step === "Pick"
				? pick(h, model)
				: model.step === "Preview"
					? preview(h, model)
					: model.step === "Review"
						? review(h, model, shared)
						: model.step === "Committing"
							? committing(h, model)
							: model.step === "History"
								? history(h, model)
								: summaryStep(h, model, shared),
			undoDialog(h, model),
		],
	),
);
